/**
 * `ShortcutMap` — GVW-155 keyboard shortcut map.
 *
 * Per brief §8: real `<table>` with `<caption>` "Keyboard shortcuts", column
 * `<th scope="col">` and row `<th scope="row">` containing `<kbd>` chords.
 * Sourced from `@/state/keyboardShortcuts` (extracted from AppShell per
 * dispatch §4.4.g) so the help page stays the single source of truth.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-155/brief.md
 */

import { useEffect, useMemo } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';
import { KEYBOARD_SHORTCUTS, getShortcutsByGroup, type KeyboardShortcut } from '@/state/keyboardShortcuts';

const GROUP_LABELS: Record<KeyboardShortcut['group'], string> = {
  global: 'Global',
  map: 'Map navigation and tools',
  'three-d': '3D mode',
  pointcloud: 'Point cloud mode',
  timeline: 'Timeline (4D scrubber)',
};

const GROUP_ORDER: KeyboardShortcut['group'][] = ['global', 'map', 'three-d', 'pointcloud', 'timeline'];

export function ShortcutMap() {
  const liveRegion = useLiveRegion();
  const groups = useMemo(() => getShortcutsByGroup(), []);

  useEffect(() => {
    document.title = 'Keyboard shortcuts · GeoViewer';
    liveRegion.announce('status', 'Keyboard shortcuts reference loaded.');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-155-heading"
      data-gvw-id="GVW-155"
      data-route="/workspace/help/shortcuts"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-155-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          keyboard
        </span>
        Keyboard shortcuts
      </h1>

      <p style={{ marginTop: 0, marginBottom: tokens.space[5], color: tokens.color.ink.secondary, fontSize: tokens.font.size.md }}>
        Shortcuts apply when the map / 3D view has focus and you are not typing into a form field.
      </p>

      <table
        style={{
          width: '100%',
          maxWidth: 960,
          borderCollapse: 'collapse',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          overflow: 'hidden',
        }}
      >
        <caption
          style={{
            padding: tokens.space[3],
            captionSide: 'top',
            textAlign: 'left',
            fontSize: tokens.font.size.lg,
            fontWeight: tokens.font.weight.medium,
            color: tokens.color.ink.primary,
          }}
        >
          Keyboard shortcuts
        </caption>
        <thead style={{ background: tokens.color.surface[2] }}>
          <tr>
            <th
              scope="col"
              style={{
                padding: tokens.space[3],
                textAlign: 'left',
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.secondary,
                borderBottom: tokens.border.thin,
                width: '20%',
              }}
            >
              Keys
            </th>
            <th
              scope="col"
              style={{
                padding: tokens.space[3],
                textAlign: 'left',
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.secondary,
                borderBottom: tokens.border.thin,
                width: '50%',
              }}
            >
              Action
            </th>
            <th
              scope="col"
              style={{
                padding: tokens.space[3],
                textAlign: 'left',
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.secondary,
                borderBottom: tokens.border.thin,
                width: '15%',
              }}
            >
              Group
            </th>
            <th
              scope="col"
              style={{
                padding: tokens.space[3],
                textAlign: 'left',
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.secondary,
                borderBottom: tokens.border.thin,
                width: '15%',
              }}
            >
              Notes
            </th>
          </tr>
        </thead>
        <tbody>
          {GROUP_ORDER.flatMap((groupKey) => {
            const items = groups.get(groupKey);
            if (!items || items.length === 0) return [];
            return items.map((shortcut, idx) => (
              <tr key={`${groupKey}-${idx}`} style={{ borderBottom: tokens.border.thin }}>
                <th
                  scope="row"
                  style={{
                    padding: tokens.space[3],
                    textAlign: 'left',
                    fontWeight: tokens.font.weight.regular,
                    verticalAlign: 'top',
                  }}
                >
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: tokens.space[1] }}>
                    {shortcut.keys.map((key) => (
                      <kbd
                        key={key}
                        style={{
                          fontFamily: tokens.font.family.mono,
                          fontSize: tokens.font.size.xs,
                          padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                          background: tokens.color.surface[2],
                          border: tokens.border.thin,
                          borderRadius: tokens.radius.chip,
                          color: tokens.color.ink.primary,
                        }}
                      >
                        {key}
                      </kbd>
                    ))}
                  </span>
                </th>
                <td style={{ padding: tokens.space[3], verticalAlign: 'top', fontSize: tokens.font.size.sm }}>
                  {shortcut.description}
                </td>
                <td style={{ padding: tokens.space[3], verticalAlign: 'top', fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
                  {GROUP_LABELS[shortcut.group]}
                </td>
                <td style={{ padding: tokens.space[3], verticalAlign: 'top', fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
                  {shortcut.conditions ?? '—'}
                </td>
              </tr>
            ));
          })}
        </tbody>
      </table>

      <p style={{ marginTop: tokens.space[5], color: tokens.color.ink.muted, fontSize: tokens.font.size.sm }}>
        Source: <code style={{ fontFamily: tokens.font.family.mono }}>state/keyboardShortcuts.ts</code>
        {' '}({KEYBOARD_SHORTCUTS.length} shortcuts)
      </p>
    </main>
  );
}
